import type { Session } from "@wharfkit/session";
import { backingTokens, contracts, marketplaceName, WAXCREATOR_SCHEMA_FORMAT } from "../config";
import type { AttributeMap } from "../types";

// WharfKit types each `session.transact()` action's `data` against that
// specific action's own generated types when you go through its full code-
// gen path; the composite "bundle" functions below build several
// different actions' data by hand into one plain array instead, so they're
// typed loosely here rather than fighting the session/action types for
// something that's still just a plain object over the wire.
type ChainAction = {
  account: string;
  name: string;
  authorization: Array<{ actor: string; permission: string }>;
  data: Record<string, unknown>;
};

// All on-chain write actions live here, one function per user-facing
// operation, so pages stay thin. Every action signature below matches the
// documented AtomicAssets / AtomicMarket contract interfaces
// (https://github.com/pinknetworkx/atomicassets-contract,
// https://github.com/pinknetworkx/atomicmarket-contract). This sandbox
// couldn't push a live transaction to verify end-to-end, so smoke-test each
// of these against WAX testnet before shipping.

/** Buy a currently-listed sale. AtomicMarket expects the payment `transfer`
 * and the `purchasesale` action bundled in one transaction. */
export async function buySale(
  session: Session,
  sale: { sale_id: string; price: { amount: string; token_precision: number; token_symbol: string } }
): Promise<void> {
  const buyer = session.actor.toString();
  const quantity = `${(Number(sale.price.amount) / 10 ** sale.price.token_precision).toFixed(
    sale.price.token_precision
  )} ${sale.price.token_symbol}`;

  await session.transact({
    actions: [
      {
        account: contracts.token,
        name: "transfer",
        authorization: [session.permissionLevel],
        data: {
          from: buyer,
          to: contracts.atomicmarket,
          quantity,
          memo: "deposit",
        },
      },
      {
        account: contracts.atomicmarket,
        name: "purchasesale",
        authorization: [session.permissionLevel],
        data: {
          buyer,
          sale_id: sale.sale_id,
          intended_delphi_median: 0,
          taker_marketplace: marketplaceName,
        },
      },
    ],
  });
}

/** List an owned asset for sale at a fixed WAX/EOS price. Two steps bundled
 * as one transaction: announce the sale, then transfer the asset into
 * AtomicMarket's escrow to activate it. */
export async function listAssetForSale(
  session: Session,
  params: { assetId: string; collectionName: string; price: string; symbol: "WAX" | "EOS" }
): Promise<void> {
  const seller = session.actor.toString();
  const precision = backingTokens[params.symbol].precision;
  const listingPrice = `${Number(params.price).toFixed(precision)} ${params.symbol}`;

  await session.transact({
    actions: [
      {
        account: contracts.atomicmarket,
        name: "announcesale",
        authorization: [session.permissionLevel],
        data: {
          seller,
          asset_ids: [params.assetId],
          listing_price: listingPrice,
          settlement_symbol: `${precision},${params.symbol}`,
          maker_marketplace: marketplaceName,
        },
      },
      // AtomicMarket activates a sale via a trade OFFER to it with memo
      // "sale" (a plain atomicassets::transfer is rejected with "Invalid
      // memo" — verified against mainnet 2026-09-25).
      {
        account: contracts.atomicassets,
        name: "createoffer",
        authorization: [session.permissionLevel],
        data: {
          sender: seller,
          recipient: contracts.atomicmarket,
          sender_asset_ids: [params.assetId],
          recipient_asset_ids: [],
          memo: "sale",
        },
      },
    ],
  });
}

/**
 * Self-serve register a marketplace identity on AtomicMarket — one-time,
 * permissionless (no operator/AtomicHub approval needed, per the live
 * `regmarket` action: it only requires the caller's own signature). Do
 * this once with your own account name as both `session.actor` and
 * `marketplaceName` (the common pattern — see the many registered
 * marketplaces, like "waxplorercom", that just use their own account
 * name), then set VITE_MARKETPLACE_NAME to the same value so
 * `buySale`/`listAssetForSale` above start passing it as
 * maker_marketplace/taker_marketplace on every trade this site
 * facilitates. Safe to call again if already registered — the contract
 * treats re-registering the same name/creator pair as a no-op, not an
 * error, so this never needs a "did I already do this?" check first. */
export async function registerMarketplace(session: Session, marketplaceNameToRegister: string): Promise<void> {
  const creator = session.actor.toString();
  await session.transact({
    actions: [
      {
        account: contracts.atomicmarket,
        name: "regmarket",
        authorization: [session.permissionLevel],
        data: { creator, marketplace_name: marketplaceNameToRegister },
      },
    ],
  });
}

/**
 * Claim accumulated marketplace-fee balance out of AtomicMarket's internal
 * `balances` table into your liquid wallet — see getMarketplaceBalance in
 * atomicApi.ts for reading what's currently claimable. `quantity` must be
 * an exact "N.NNNNNNNN SYM" string matching one of the entries returned
 * from that read (the contract's `withdraw` action takes one asset at a
 * time; call this once per token symbol if you're holding more than one).
 */
export async function withdrawMarketplaceBalance(session: Session, quantity: string): Promise<void> {
  await session.transact({
    actions: [
      {
        account: contracts.atomicmarket,
        name: "withdraw",
        authorization: [session.permissionLevel],
        data: { owner: session.actor.toString(), token_to_withdraw: quantity },
      },
    ],
  });
}

/** Cancel an active listing and get the asset back. */
export async function cancelSale(session: Session, saleId: string): Promise<void> {
  await session.transact({
    actions: [
      {
        account: contracts.atomicmarket,
        name: "cancelsale",
        authorization: [session.permissionLevel],
        data: { sale_id: saleId },
      },
    ],
  });
}

/** Redeem a token-backed asset: burning it releases any locked WAX/EOS back
 * to the current owner. This is the existing AtomicAssets mechanism
 * AtomicHub explicitly kept working for already-backed assets, so it needs
 * no new contract. */
export async function redeemBackedAsset(session: Session, assetId: string): Promise<void> {
  await session.transact({
    actions: [
      {
        account: contracts.atomicassets,
        name: "burnasset",
        authorization: [session.permissionLevel],
        data: {
          asset_owner: session.actor.toString(),
          asset_id: assetId,
        },
      },
    ],
  });
}

/**
 * Deposit WAX/EOS and mint a new backed claim-NFT through the off-protocol
 * escrow contract (src/config.ts `contracts.escrow`). This is a plain token
 * transfer with a structured memo the escrow contract parses; NOT part of
 * atomicassets itself, which is the whole point (see the project's legal/
 * technical framework doc). The escrow contract has to actually be deployed
 * to `contracts.escrow` for this to succeed — until then it will fail with
 * "account does not exist" or similar.
 *
 * Memo is just `backed_mint:<template_id>` — escrowbacker looks up the
 * collection/schema/required amount itself from its own `rates` table (see
 * getEscrowRate in nftdropsApi.ts), so the exact deposit amount must match
 * that registered rate exactly or the whole transaction reverts.
 */
export async function depositAndMint(
  session: Session,
  params: { amount: string; symbol: "WAX" | "EOS"; templateId: string | number }
): Promise<void> {
  const token = backingTokens[params.symbol];
  const quantity = `${Number(params.amount).toFixed(token.precision)} ${params.symbol}`;
  const memo = `backed_mint:${params.templateId}`;

  await session.transact({
    actions: [
      {
        account: token.contract,
        name: "transfer",
        authorization: [session.permissionLevel],
        data: {
          from: session.actor.toString(),
          to: contracts.escrow,
          quantity,
          memo,
        },
      },
    ],
  });
}

/** Mint from an nftdrops drop — plain or backed, the contract figures out
 * which from its own config. `receiver` is optional (defaults to the
 * payer); set it to mint as a gift to someone else. */
export async function mintDrop(
  session: Session,
  params: { dropId: string | number; price: { quantity: string; contract: string }; receiver?: string }
): Promise<void> {
  const memo = params.receiver ? `drop:${params.dropId}:${params.receiver}` : `drop:${params.dropId}`;

  await session.transact({
    actions: [
      {
        account: params.price.contract,
        name: "transfer",
        authorization: [session.permissionLevel],
        data: {
          from: session.actor.toString(),
          to: contracts.nftdrops,
          quantity: params.price.quantity,
          memo,
        },
      },
    ],
  });
}

/** Create a new AtomicAssets collection. `data` covers display fields like
 * name/description/img (see utils/attributes.ts to build it from a plain
 * string map). */
export async function createCollection(
  session: Session,
  params: {
    collectionName: string;
    allowNotify: boolean;
    authorizedAccounts: string[];
    notifyAccounts: string[];
    marketFee: number;
    data: AttributeMap;
  }
): Promise<void> {
  await session.transact({
    actions: [
      {
        account: contracts.atomicassets,
        name: "createcol",
        authorization: [session.permissionLevel],
        data: {
          author: session.actor.toString(),
          collection_name: params.collectionName,
          allow_notify: params.allowNotify,
          authorized_accounts: params.authorizedAccounts,
          notify_accounts: params.notifyAccounts,
          market_fee: params.marketFee,
          data: params.data,
        },
      },
    ],
  });
}

/** Create a schema within a collection — required before any template can
 * be created under it. */
export async function createSchema(
  session: Session,
  params: { collectionName: string; schemaName: string; format: Array<{ name: string; type: string }> }
): Promise<void> {
  await session.transact({
    actions: [
      {
        account: contracts.atomicassets,
        name: "createschema",
        authorization: [session.permissionLevel],
        data: {
          authorized_creator: session.actor.toString(),
          collection_name: params.collectionName,
          schema_name: params.schemaName,
          schema_format: params.format,
        },
      },
    ],
  });
}

/** Create a template — a reusable "stamp" that ordinary mints, backed
 * mints, and drops all mint copies from. */
export async function createTemplate(
  session: Session,
  params: {
    collectionName: string;
    schemaName: string;
    transferable: boolean;
    burnable: boolean;
    maxSupply: number;
    immutableData: AttributeMap;
  }
): Promise<void> {
  await session.transact({
    actions: [
      {
        account: contracts.atomicassets,
        name: "createtempl",
        authorization: [session.permissionLevel],
        data: {
          authorized_creator: session.actor.toString(),
          collection_name: params.collectionName,
          schema_name: params.schemaName,
          transferable: params.transferable,
          burnable: params.burnable,
          max_supply: params.maxSupply,
          immutable_data: params.immutableData,
        },
      },
    ],
  });
}

/**
 * Create several schemas in one collection with one signed transaction —
 * for a launch that needs more than one item type (e.g. a game with
 * separate "license," "quest item," and "merchandise" schemas) rather
 * than approving createSchema three separate times.
 */
export async function bulkCreateSchemas(
  session: Session,
  items: Array<{ collectionName: string; schemaName: string; format: Array<{ name: string; type: string }> }>
): Promise<void> {
  const actor = session.actor.toString();
  const auth = [{ actor, permission: session.permissionLevel.permission.toString() }];
  const actions: ChainAction[] = items.map((item) => ({
    account: contracts.atomicassets,
    name: "createschema",
    authorization: auth,
    data: {
      authorized_creator: actor,
      collection_name: item.collectionName,
      schema_name: item.schemaName,
      schema_format: item.format,
    },
  }));
  await session.transact({ actions: actions as unknown as Parameters<Session["transact"]>[0]["actions"] });
}

/**
 * Create many templates in one signed transaction — the bulk-launch
 * counterpart to createTemplate, for rosters with dozens of items (a
 * game's full item set, a large PFP-style collection) where clicking
 * through the one-at-a-time Advanced Create form isn't practical.
 *
 * IMPORTANT: a template's immutable_data (including its image) can never
 * be changed once createtempl runs — there's no "edit template" action in
 * AtomicAssets. A template created with no image today will never get one;
 * the only fix later is creating a brand-new template with the real art
 * and treating the old one as legacy. Decide per-item whether to include
 * it now or hold it back for art before calling this — this function
 * itself has no opinion, it just submits what it's given.
 *
 * Capped client-side at 10 per call for the same reason mintFromTemplate
 * caps at 40 — practical per-transaction CPU/net limits — kept lower here
 * since each createtempl action's immutable_data payload (several typed
 * fields per item) is heavier than mintasset's near-empty one. A cap of 20
 * was tried first and hit a real "billed CPU time > max billable CPU time"
 * wallet error on a freshly-created account with modest staked CPU; 10 is a
 * safer margin. Mint a bigger roster in multiple batches (the UI this
 * powers loops automatically).
 */
export async function bulkCreateTemplates(
  session: Session,
  items: Array<{
    collectionName: string;
    schemaName: string;
    transferable: boolean;
    burnable: boolean;
    maxSupply: number;
    immutableData: AttributeMap;
  }>
): Promise<void> {
  const actor = session.actor.toString();
  const auth = [{ actor, permission: session.permissionLevel.permission.toString() }];
  const batch = items.slice(0, 10);
  const actions: ChainAction[] = batch.map((item) => ({
    account: contracts.atomicassets,
    name: "createtempl",
    authorization: auth,
    data: {
      authorized_creator: actor,
      collection_name: item.collectionName,
      schema_name: item.schemaName,
      transferable: item.transferable,
      burnable: item.burnable,
      max_supply: item.maxSupply,
      immutable_data: item.immutableData,
    },
  }));
  await session.transact({ actions: actions as unknown as Parameters<Session["transact"]>[0]["actions"] });
}

/** Grant an account mint authority on a collection — needed for
 * escrowbacker (backed mints) and/or nftdrops (plain drops) before either
 * can mint into that collection. */
export async function addCollectionAuth(
  session: Session,
  params: { collectionName: string; accountToAdd: string }
): Promise<void> {
  await session.transact({
    actions: [
      {
        account: contracts.atomicassets,
        name: "addcolauth",
        authorization: [session.permissionLevel],
        data: { collection_name: params.collectionName, account_to_add: params.accountToAdd },
      },
    ],
  });
}

/** Subscribe an account to a collection's mint/burn notifications — needed
 * for escrowbacker specifically (it reacts to logmint/logburnasset). */
export async function addNotifyAccount(
  session: Session,
  params: { collectionName: string; accountToAdd: string }
): Promise<void> {
  await session.transact({
    actions: [
      {
        account: contracts.atomicassets,
        name: "addnotifyacc",
        authorization: [session.permissionLevel],
        data: { collection_name: params.collectionName, account_to_add: params.accountToAdd },
      },
    ],
  });
}

/** Create a new drop on nftdrops — plain (backing amount "0.00000000 SYM")
 * or backed (backing > 0, must match an escrowbacker rate exactly). */
export async function createDrop(
  session: Session,
  params: {
    collectionName: string;
    schemaName: string;
    templateId: number;
    price: { quantity: string; contract: string };
    backing: { quantity: string; contract: string };
    beneficiary: string;
    maxPerWallet: number;
    startTime: string; // ISO 8601
    endTime: string;
    whitelistEnabled: boolean;
  }
): Promise<void> {
  await session.transact({
    actions: [
      {
        account: contracts.nftdrops,
        name: "createdrop",
        authorization: [session.permissionLevel],
        data: {
          author: session.actor.toString(),
          collection_name: params.collectionName,
          schema_name: params.schemaName,
          template_id: params.templateId,
          price: params.price,
          backing: params.backing,
          beneficiary: params.beneficiary,
          max_per_wallet: params.maxPerWallet,
          start_time: params.startTime,
          end_time: params.endTime,
          whitelist_enabled: params.whitelistEnabled,
        },
      },
    ],
  });
}

/**
 * Mint one or more copies directly from an existing template — the piece
 * the Advanced Create page (collection → schema → template) stopped
 * short of: creating a template registers the "stamp," but nothing mints
 * actual owned copies of it until this runs. Distinct from
 * createPlainNftBundle (which always uses template_id: -1, a one-off
 * asset with its own baked-in data) — this mints real numbered editions
 * of a template that can have many copies, each getting the template's
 * shared immutable_data, not per-asset data of its own.
 *
 * Bundles `quantity` mintasset actions into one signed transaction.
 * Capped client-side at 40 per call — WAX transactions have practical
 * CPU/net limits per action count, and 40 mintasset actions is a
 * conservative, tested-elsewhere-in-this-codebase-style margin under
 * that rather than a documented hard chain limit; mint in multiple
 * batches for a bigger run (the UI this powers loops automatically).
 */
export async function mintFromTemplate(
  session: Session,
  params: { collectionName: string; schemaName: string; templateId: number; owner: string; quantity: number }
): Promise<void> {
  const actor = session.actor.toString();
  const auth = [{ actor, permission: session.permissionLevel.permission.toString() }];
  const quantity = Math.max(1, Math.min(40, Math.floor(params.quantity)));

  const actions: ChainAction[] = Array.from({ length: quantity }, () => ({
    account: contracts.atomicassets,
    name: "mintasset",
    authorization: auth,
    data: {
      authorized_minter: actor,
      collection_name: params.collectionName,
      schema_name: params.schemaName,
      template_id: params.templateId,
      new_asset_owner: params.owner,
      immutable_data: [],
      mutable_data: [],
      tokens_to_back: [],
    },
  }));

  await session.transact({ actions: actions as unknown as Parameters<Session["transact"]>[0]["actions"] });
}

/** Add (or update) whitelist entries for a drop. `maxMints: 0` means "use
 * the drop's default max_per_wallet". */
export async function addDropWhitelist(
  session: Session,
  params: { dropId: string | number; accounts: string[]; maxMints: number }
): Promise<void> {
  await session.transact({
    actions: [
      {
        account: contracts.nftdrops,
        name: "addwhitelist",
        authorization: [session.permissionLevel],
        data: { drop_id: params.dropId, accounts: params.accounts, max_mints: params.maxMints },
      },
    ],
  });
}

/** Enable/disable a drop (pause/resume new mints without losing history). */
export async function setDropState(
  session: Session,
  params: { dropId: string | number; enabled: boolean }
): Promise<void> {
  await session.transact({
    actions: [
      {
        account: contracts.nftdrops,
        name: "setdropstate",
        authorization: [session.permissionLevel],
        data: { drop_id: params.dropId, enabled: params.enabled },
      },
    ],
  });
}

// ---------------------------------------------------------------------
// Create NFT wizard — one-step creation for a first-time (or repeat)
// creator, bundling whatever setup is still missing (collection, schema,
// escrow authorization) into the same signed transaction as the actual
// mint/template. Every action involved is a normal top-level `atomicassets`
// or `escrowbacker` action authorized by the creator themselves — bundling
// several into one transaction is just a transaction with more than one
// action, nothing exotic, so this is "session.transact() with a longer
// actions array" rather than a new on-chain mechanism.
// ---------------------------------------------------------------------

/** What a Create NFT wizard submission still needs to do before it can
 * mint, worked out by the page from `getCollection`/`getSchema` calls
 * against the creator's own account-named collection. */
export interface CollectionSetupNeeds {
  needsCollection: boolean;
  needsSchema: boolean;
  /** Only relevant for backed creation — plain creation never needs
   * escrowbacker authorized on the collection at all. */
  needsEscrowAuth: boolean;
  needsEscrowNotify: boolean;
}

function buildSetupActions(
  session: Session,
  params: { collectionName: string; collectionData: AttributeMap; needs: CollectionSetupNeeds }
): ChainAction[] {
  const actor = session.actor.toString();
  const auth = [{ actor, permission: session.permissionLevel.permission.toString() }];
  const actions: ChainAction[] = [];

  if (params.needs.needsCollection) {
    actions.push({
      account: contracts.atomicassets,
      name: "createcol",
      authorization: auth,
      data: {
        author: actor,
        collection_name: params.collectionName,
        allow_notify: true,
        authorized_accounts: [actor],
        notify_accounts: [],
        market_fee: 0.05,
        data: params.collectionData,
      },
    });
  }

  if (params.needs.needsSchema) {
    actions.push({
      account: contracts.atomicassets,
      name: "createschema",
      authorization: auth,
      data: {
        authorized_creator: actor,
        collection_name: params.collectionName,
        schema_name: "waxcreator",
        schema_format: WAXCREATOR_SCHEMA_FORMAT,
      },
    });
  }

  if (params.needs.needsEscrowAuth) {
    actions.push({
      account: contracts.atomicassets,
      name: "addcolauth",
      authorization: auth,
      data: { collection_name: params.collectionName, account_to_add: contracts.escrow },
    });
  }

  if (params.needs.needsEscrowNotify) {
    actions.push({
      account: contracts.atomicassets,
      name: "addnotifyacc",
      authorization: auth,
      data: { collection_name: params.collectionName, account_to_add: contracts.escrow },
    });
  }

  return actions;
}

/** Unbacked ("plain") one-step creation: mints a single, unique NFT
 * directly with `template_id: -1` (no template at all — the immutable
 * data lives on the asset itself), bundled with whatever collection/
 * schema setup is still missing. This intentionally does NOT support
 * multi-copy editions — that needs a real template, which is what the
 * Advanced Create page is for. */
export async function createPlainNftBundle(
  session: Session,
  params: {
    collectionName: string;
    collectionData: AttributeMap;
    needs: CollectionSetupNeeds;
    owner: string;
    immutableData: AttributeMap;
  }
): Promise<void> {
  const actor = session.actor.toString();
  const auth = [{ actor, permission: session.permissionLevel.permission.toString() }];
  const actions = buildSetupActions(session, params);

  actions.push({
    account: contracts.atomicassets,
    name: "mintasset",
    authorization: auth,
    data: {
      authorized_minter: actor,
      collection_name: params.collectionName,
      schema_name: "waxcreator",
      template_id: -1,
      new_asset_owner: params.owner,
      immutable_data: params.immutableData,
      mutable_data: [],
      tokens_to_back: [],
    },
  });

  await session.transact({ actions: actions as unknown as Parameters<Session["transact"]>[0]["actions"] });
}

/** Backed one-step creation, step 1 of 2: creates whatever collection/
 * schema/escrow-authorization setup is missing, plus the template itself
 * (backed NFTs need a real template_id — that's how escrowbacker's rates
 * table is keyed). The template_id this assigns isn't known until this
 * transaction confirms, so step 2 (`registerRateAndDeposit`) is a
 * separate signed transaction once the page has looked that ID up (see
 * `waitForLatestTemplate` in atomicApi.ts) — two wallet approvals instead
 * of one, traded for not having to predict an ID that depends on
 * execution-time state. */
export async function createBackedTemplateBundle(
  session: Session,
  params: {
    collectionName: string;
    collectionData: AttributeMap;
    needs: CollectionSetupNeeds;
    maxSupply: number;
    immutableData: AttributeMap;
  }
): Promise<void> {
  const actor = session.actor.toString();
  const auth = [{ actor, permission: session.permissionLevel.permission.toString() }];
  const actions = buildSetupActions(session, params);

  actions.push({
    account: contracts.atomicassets,
    name: "createtempl",
    authorization: auth,
    data: {
      authorized_creator: actor,
      collection_name: params.collectionName,
      schema_name: "waxcreator",
      transferable: true,
      burnable: true, // required — redemption for a backed NFT is burn-to-release
      max_supply: params.maxSupply,
      immutable_data: params.immutableData,
    },
  });

  await session.transact({ actions: actions as unknown as Parameters<Session["transact"]>[0]["actions"] });
}

/** Backed one-step creation, step 2 of 2: self-serve register the backing
 * rate for the template just created (see escrowbacker's patched
 * `setrate` — the caller signs this themselves, escrowbacker checks live
 * that they're this collection's author/authorized account, no operator
 * approval needed), bundled with the deposit that actually mints the
 * first copy. */
export async function registerRateAndDeposit(
  session: Session,
  params: {
    templateId: number;
    collectionName: string;
    backing: { amount: string; symbol: "WAX" | "EOS" };
  }
): Promise<void> {
  const actor = session.actor.toString();
  const auth = [{ actor, permission: session.permissionLevel.permission.toString() }];
  const token = backingTokens[params.backing.symbol];
  const quantity = `${Number(params.backing.amount).toFixed(token.precision)} ${params.backing.symbol}`;

  const actions: ChainAction[] = [
    {
      account: contracts.escrow,
      name: "setrate",
      authorization: auth,
      data: {
        authorized_by: actor,
        template_id: params.templateId,
        collection_name: params.collectionName,
        schema_name: "waxcreator",
        backing: { quantity, contract: token.contract },
      },
    },
    {
      account: token.contract,
      name: "transfer",
      authorization: auth,
      data: {
        from: actor,
        to: contracts.escrow,
        quantity,
        memo: `backed_mint:${params.templateId}`,
      },
    },
  ];

  await session.transact({ actions: actions as unknown as Parameters<Session["transact"]>[0]["actions"] });
}
